import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import type { RefObject } from 'react'

import {
  bodyColumnWindow,
  HORIZONTAL_OVERSCAN_PX,
  HORIZONTAL_WINDOW_MIN_COLUMNS,
  isSameColumnWindow
} from './treadmillMath'
import type { ColumnLayout, ColumnWindow } from './treadmillMath'

export const useColumnWindow = <T>(
  scrollerRef: RefObject<HTMLDivElement | null>,
  layout: ColumnLayout<T>,
  columnCount: number
) => {
  const [columnWindow, setColumnWindow] = useState<ColumnWindow>()
  const frameRef = useRef<number | null>(null)
  const isWindowed = columnCount >= HORIZONTAL_WINDOW_MIN_COLUMNS
  const bodyWidths = useMemo(
    () => layout.body.map(({ width }) => width),
    [layout]
  )

  const updateColumnWindow = useCallback(() => {
    frameRef.current = null

    const scroller = scrollerRef.current

    if (!isWindowed || !scroller) {
      setColumnWindow(undefined)

      return
    }

    const visibleStart =
      scroller.scrollLeft - layout.bodyOffset - HORIZONTAL_OVERSCAN_PX
    const visibleEnd =
      scroller.scrollLeft +
      scroller.clientWidth -
      layout.bodyOffset +
      HORIZONTAL_OVERSCAN_PX
    const next = bodyColumnWindow(
      layout.bodyLefts,
      bodyWidths,
      visibleStart,
      visibleEnd
    )

    setColumnWindow((previous) =>
      isSameColumnWindow(previous, next) ? previous : next
    )
  }, [scrollerRef, isWindowed, layout, bodyWidths])

  const scheduleColumnWindow = useCallback(() => {
    if (isWindowed && frameRef.current === null) {
      frameRef.current = requestAnimationFrame(updateColumnWindow)
    }
  }, [isWindowed, updateColumnWindow])

  useLayoutEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    updateColumnWindow()

    const scroller = scrollerRef.current

    if (!isWindowed || !scroller || typeof ResizeObserver === 'undefined') {
      return
    }

    const resizeObserver = new ResizeObserver(scheduleColumnWindow)
    resizeObserver.observe(scroller)

    return () => resizeObserver.disconnect()
  }, [scrollerRef, isWindowed, updateColumnWindow, scheduleColumnWindow])

  useEffect(
    () => () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current)
      }
    },
    []
  )

  return { columnWindow, scheduleColumnWindow }
}
